import { EditOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { groupBy } from 'lodash';
import { useState } from 'react';
import colorTokens from '../../../constants/design_system/colors';
import { useIsAllTasksView } from '../../../hooks/useIsAllTasksView';
import { SpaceSettingsTab } from '../../../pages/Space/SpaceSettingsModal/SpaceSettingsModal';
import { useGetActualSpaceId, useGetWorkspaceById } from '../../../serverState/workspace';
import { useSpaceSettingsStore } from '../../../stores/spaceSettingsStore';
import {
  defaultTaskStatuses,
  sortTaskStatusesByOrder,
  TaskStatus,
  TaskStatusGroup,
  TaskStatusGroupUtils,
} from '../../../types/TaskStatus';
import StatusLabel, { StatusComponentType } from '../StatusLabel';
import BaseDropdown from './BaseDropdown';
import './ItemDropdown.scss';
import NoDragWrapper from './NoDragWrapper';

export type TaskStatusDropdownProps = {
  taskStatusId: number;
  onSelect: (taskStatus: TaskStatus) => void;
  onBlur?: () => void;
  disabled?: boolean;
  size: 'small' | 'medium' | 'large';
  hideOutline?: boolean;
  defaultOpen?: boolean;
  className?: string;
  position?: 'bottom' | 'bottomLeft' | 'bottomRight' | 'topCenter' | 'topLeft' | 'topRight';
  /** Source statuses from this space instead of the active route workspace (e.g. All Tasks cross-space view). */
  workspaceId?: number;
  /** Component type to use for the trigger label (controls max-width / tooltip). Defaults to Dropdown. */
  triggerComponentType?: StatusComponentType;
};

const TaskStatusDropdown = (props: TaskStatusDropdownProps) => {
  const activeSpaceId = useGetActualSpaceId();
  const isAllTasksView = useIsAllTasksView();
  // Manage Statuses routes into a single space's settings, so it must be hidden in
  // the cross-space All Tasks view (#2683). activeSpaceId alone is not enough: it is
  // the persisted last-visited space and stays set inside All Tasks.
  const canManageStatuses = !!activeSpaceId && !isAllTasksView;
  const effectiveWorkspaceId = props.workspaceId ?? (activeSpaceId ? Number(activeSpaceId) : null);
  const workspaceQuery = useGetWorkspaceById(effectiveWorkspaceId);
  const statusOptions: TaskStatus[] = sortTaskStatusesByOrder(
    (workspaceQuery.data?.TaskStatus as TaskStatus[]) || defaultTaskStatuses
  );
  const groupedOptions = groupBy(statusOptions, 'group');
  const taskStatus = statusOptions.find((status) => status.id === props.taskStatusId);

  const [isDropdownOpen, setDropdownOpen] = useState(props.defaultOpen || false);
  const [scrollKey, setScrollKey] = useState(0);

  const requestOpenSpaceSettings = useSpaceSettingsStore((state) => state.requestOpen);

  const onSelect = (taskStatus: TaskStatus) => {
    if (props.onSelect) {
      props.onSelect(taskStatus);
    }
    isDropdownOpen && setDropdownOpen(false);
  };

  return (
    <NoDragWrapper className="h-full">
      <BaseDropdown
        disableTransition
        trigger={['click']}
        open={isDropdownOpen}
        disabled={props.disabled}
        placement={props.position || 'bottomLeft'}
        onOpenChange={(open) => {
          if (open) setScrollKey((k) => k + 1);
          setDropdownOpen(open);
          if (!open && props.onBlur) {
            props.onBlur();
          }
        }}
        dropdownRender={() => (
          <div className="flex flex-col items-start justify-start bg-brand-white shadow-md rounded-lg p-2">
            <div
              key={scrollKey}
              className="flex flex-col items-start justify-start gap-2 w-full max-h-[300px] overflow-y-auto"
            >
              {Object.values(TaskStatusGroup).map((group) => (
                <div
                  key={group}
                  className="w-full text-xs font-semibold font-['Figtree'] text-neutral-900 flex flex-col gap-1"
                >
                  {TaskStatusGroupUtils.getTitle(group)}
                  {groupedOptions[group]?.map((status) => (
                    <div
                      key={status.title}
                      className="flex items-center w-full cursor-pointer hover:bg-neutral-100"
                      onClick={() => onSelect(status)}
                    >
                      <StatusLabel
                        color={status.color}
                        title={status.title}
                        group={status.group}
                        component={StatusComponentType.Dropdown}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
            {/* Manage Statuses: only in a space route, not cross-space All Tasks (#2683
                — that view has no single space settings, so no Manage Statuses entry). */}
            {/* `box-border` on the row below is load-bearing: it is `content-box`, so
                its `w-full` (100% = the card's 160px content width) had the 8px of
                `px-1` and the 3px border added on top — 171px, overflowing the card
                by exactly 11px, which is what showed as the row sticking out past the
                popup's right edge. #2838 */}
            {canManageStatuses && (
              <div className="flex flex-nowrap px-1 pb-1 pt-1 justify-start w-full box-border border-t border-solid border-neutral-200 mt-2">
                <Button
                  className="text-sm font-normal text-neutral-900 border-none pl-1 shadow-none w-full text-left"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    requestOpenSpaceSettings(SpaceSettingsTab.STATUSES);
                    setDropdownOpen(false);
                    if (props.onBlur) {
                      props.onBlur();
                    }
                  }}
                  icon={<EditOutlined />}
                >
                  Manage Statuses
                </Button>
              </div>
            )}
          </div>
        )}
      >
        <div
          className={`${props.className ?? 'h-full'} flex items-center cursor-pointer`}
          style={{ outline: isDropdownOpen && !props.hideOutline ? `2px solid ${colorTokens.green[300]}` : 'none' }}
        >
          <div className="relative parent-task">
            {taskStatus && (
              <StatusLabel
                group={taskStatus.group}
                color={taskStatus.color}
                title={taskStatus.title}
                component={props.triggerComponentType ?? StatusComponentType.Dropdown}
              />
            )}
          </div>
        </div>
      </BaseDropdown>
    </NoDragWrapper>
  );
};

export default TaskStatusDropdown;
